import { useState, type ReactNode } from 'react';
import { History, PackagePlus, Wallet, Ban, Repeat } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { Field, PaymentModePicker, money, shortDateTime } from '../outpatient/ui';
import type { PaymentMode } from '../types';
import {
  useBorrowIssue, useRecordBorrowReturn, useRecordBorrowPayment, useCancelBorrowIssue, useChangeBorrowSettlement,
  useBorrowSettings,
} from './useBorrow';
import {
  BorrowStatusBadges, ErrorBanner, SETTLEMENT_LABEL, addDaysYmd, dateOnly, expiryLabel, localDate, ymd,
} from './ui';
import type { BorrowIssueDetail, BorrowSettings, SettlementType } from './types';

const errorOf = (e: unknown) => (e instanceof ApiError ? e.message : null);

const EVENT_LABEL: Record<string, string> = {
  issue: 'Issued',
  return: 'Item returned',
  payment: 'Payment recorded',
  cancel: 'Cancelled',
  settlement_change: 'Settlement type changed',
};

export default function BorrowDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const query = useBorrowIssue(id);
  const issue = query.data;
  const settings = useBorrowSettings().data;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-[720px] overflow-y-auto">
        {query.isLoading || !issue || !settings ? (
          <LoadingState />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                <span className="font-mono">{issue.issue_number}</span>
                <BorrowStatusBadges issue={issue} />
              </DialogTitle>
            </DialogHeader>
            <DetailBody issue={issue} settings={settings} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailBody({ issue, settings }: { issue: BorrowIssueDetail; settings: BorrowSettings }) {
  const open = ['issued', 'partially_returned', 'partially_paid'].includes(issue.status);
  const untouched = issue.status === 'issued';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-3">
        <Field label="Hospital">{issue.hospital_name}</Field>
        <Field label="Contact person">{issue.contact_person}</Field>
        <Field label="Contact number">{issue.contact_number}</Field>
        <Field label="Item" className="col-span-2">{issue.item_name}</Field>
        <Field label="Batch / expiry"><span className="font-mono">{issue.batch_number}</span> · {expiryLabel(issue.expiry_date)}</Field>
        <Field label="Quantity">{issue.quantity}</Field>
        <Field label="Item value">{money(issue.item_value)}</Field>
        <Field label="Settlement">{SETTLEMENT_LABEL[issue.settlement_type]}</Field>
        <Field label="Issue date">{dateOnly(issue.issue_date)}</Field>
        <Field label="Expected return">{dateOnly(issue.expected_return_date)}</Field>
        <Field label="Issued by">{issue.issued_by}</Field>
        <Field label="Reason" className="col-span-2 sm:col-span-3">{issue.reason}</Field>
        {issue.notes && <Field label="Notes" className="col-span-2 sm:col-span-3">{issue.notes}</Field>}
        {issue.status === 'cancelled' && (
          <Field label="Cancelled" className="col-span-2 sm:col-span-3">
            {shortDateTime(issue.cancelled_at)} by {issue.cancelled_by} — {issue.cancel_reason}
          </Field>
        )}
      </div>

      {/* ── Outstanding ─────────────────────────────────────── */}
      {issue.status !== 'cancelled' && (
        <div className="rounded-lg bg-canvas px-3 py-2.5 text-[12.5px]">
          {issue.settlement_type === 'item_return' ? (
            <>Returned <strong>{issue.returned_quantity}</strong> of {issue.quantity} · <strong>{issue.outstanding_quantity}</strong> still due back</>
          ) : (
            <>Paid <strong>{money(issue.paid_amount)}</strong> of {money(issue.item_value)} · <strong>{money(issue.outstanding_amount)}</strong> outstanding</>
          )}
          {issue.closed_at && <span className="ml-2 text-success-ink">· Closed {shortDateTime(issue.closed_at)}</span>}
        </div>
      )}

      {open && issue.settlement_type === 'item_return' && <ReturnForm key={issue.returned_quantity} issue={issue} settings={settings} />}
      {open && issue.settlement_type === 'payment' && <PaymentForm key={issue.paid_amount} issue={issue} settings={settings} />}
      {untouched && <Corrections issue={issue} settings={settings} />}

      {issue.returns.length > 0 && (
        <Section title="Returns received" icon={<PackagePlus size={12} />}>
          {issue.returns.map((r) => (
            <div key={r.id} className="rounded-lg border border-border px-3 py-2 text-[12px]">
              <div className="flex justify-between gap-2 font-semibold">
                <span>+{r.quantity} units · batch <span className="font-mono">{r.batch_number}</span> · exp {expiryLabel(r.expiry_date)}</span>
                <span>{dateOnly(r.returned_on)}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">Received by {r.received_by}{r.notes ? ` · ${r.notes}` : ''}</div>
            </div>
          ))}
        </Section>
      )}

      {issue.payments.length > 0 && (
        <Section title="Payments received" icon={<Wallet size={12} />}>
          {issue.payments.map((p) => (
            <div key={p.id} className="rounded-lg border border-border px-3 py-2 text-[12px]">
              <div className="flex justify-between gap-2 font-semibold">
                <span>{money(p.amount)} · {p.payment_mode}{p.reference_number ? ` · ref ${p.reference_number}` : ''}</span>
                <span>{dateOnly(p.paid_on)}</span>
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">Received by {p.received_by}{p.notes ? ` · ${p.notes}` : ''}</div>
            </div>
          ))}
        </Section>
      )}

      <Section title="Audit history" icon={<History size={12} />}>
        <ol className="space-y-1 text-[12px]">
          {issue.history.map((h) => (
            <li key={h.id} className="flex flex-wrap gap-x-2">
              <span className="text-ink-muted">{shortDateTime(h.changed_at)}</span>
              <span className="font-semibold">{EVENT_LABEL[h.details?.event ?? ''] ?? h.action}</span>
              <span className="text-ink-muted">by {h.changed_by}</span>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <div className="border-t border-line pt-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{icon} {title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// ── Same-item return ─────────────────────────────────────────────

function ReturnForm({ issue, settings }: { issue: BorrowIssueDetail; settings: BorrowSettings }) {
  const [qty, setQty] = useState(String(issue.outstanding_quantity));
  const [sameBatch, setSameBatch] = useState(true);
  const [batchNumber, setBatchNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [returnedOn, setReturnedOn] = useState(localDate());
  const [verified, setVerified] = useState(false);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');
  const mutation = useRecordBorrowReturn();
  // Policy: returned stock needs at least this much shelf life left (expired is always refused).
  const minExpiry = addDaysYmd(localDate(), settings.min_return_expiry_days);
  const originalTooShort = ymd(issue.expiry_date) < minExpiry;

  const submit = () => {
    const n = parseInt(qty, 10);
    if (!Number.isInteger(n) || n <= 0 || String(n) !== qty.trim()) return setFormError('Enter a whole number of units.');
    if (n > issue.outstanding_quantity) return setFormError(`Only ${issue.outstanding_quantity} unit(s) are still due back.`);
    if (!sameBatch && (!batchNumber.trim() || !expiry)) return setFormError('Enter the returned batch number and its expiry date.');
    if (!sameBatch && expiry < localDate()) return setFormError('The returned batch has expired — it cannot go back on the shelf.');
    if ((sameBatch ? ymd(issue.expiry_date) : expiry) < minExpiry) {
      return setFormError(`Hospital policy requires at least ${settings.min_return_expiry_days} day(s) of shelf life (expiry on or after ${minExpiry}).`);
    }
    if (!verified) return setFormError('Tick to confirm the item and batch have been checked.');
    setFormError('');
    mutation.mutate(
      {
        id: issue.id,
        input: {
          quantity: n,
          verified: true,
          returned_on: returnedOn,
          notes: notes.trim() || undefined,
          ...(sameBatch ? {} : { batch_number: batchNumber.trim(), expiry_date: expiry }),
        },
      },
      { onSuccess: () => { setVerified(false); setNotes(''); } }
    );
  };

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2.5 flex items-center gap-1.5 text-[13px] font-semibold"><PackagePlus size={14} /> Record returned item</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label="Returned quantity" required>
          <Input type="number" min={1} max={issue.outstanding_quantity} value={qty} onChange={(e) => setQty(e.target.value)} />
        </FormField>
        <FormField label="Return date" required>
          <Input type="date" min={ymd(issue.issue_date)} max={localDate()} value={returnedOn} onChange={(e) => setReturnedOn(e.target.value)} />
        </FormField>
        <div>
          <div className="lbl">Batch returned</div>
          {settings.allow_different_batch_return ? (
            <label className="flex h-9 cursor-pointer items-center gap-2 text-[12.5px]">
              <Checkbox checked={sameBatch} onCheckedChange={(v) => setSameBatch(v === true)} />
              Same batch ({issue.batch_number})
            </label>
          ) : (
            <div className="flex h-9 items-center text-[12.5px]" title="Hospital policy accepts only the batch that was lent">
              Batch {issue.batch_number} only
            </div>
          )}
        </div>
        {!sameBatch && (
          <>
            <FormField label="Returned batch no." required>
              <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} />
            </FormField>
            <FormField label="Returned batch expiry" required>
              <Input type="date" min={minExpiry} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
            </FormField>
          </>
        )}
        <FormField label="Notes" className="sm:col-span-3">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </div>
      {sameBatch && originalTooShort && settings.min_return_expiry_days > 0 && (
        <div className="mt-2.5 rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
          The original batch expires before {minExpiry} — hospital policy needs at least {settings.min_return_expiry_days} day(s) of shelf life on returned stock.
          {settings.allow_different_batch_return ? ' Accept a fresher batch instead.' : ''}
        </div>
      )}
      <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-[12.5px]">
        <Checkbox checked={verified} onCheckedChange={(v) => setVerified(v === true)} className="mt-0.5" />
        <span>I have verified the returned item is <strong>{issue.item_name}</strong> and the batch/expiry match what is entered above.</span>
      </label>
      <div className="mt-2.5 space-y-2">
        <ErrorBanner message={formError || errorOf(mutation.error)} />
        <Button full loading={mutation.isPending} onClick={submit}>Record return &amp; add to stock</Button>
      </div>
    </div>
  );
}

// ── Payment ──────────────────────────────────────────────────────

function PaymentForm({ issue, settings }: { issue: BorrowIssueDetail; settings: BorrowSettings }) {
  const outstanding = parseFloat(issue.outstanding_amount);
  const [amount, setAmount] = useState(outstanding.toFixed(2));
  const [mode, setMode] = useState<PaymentMode>(settings.allowed_payment_modes[0] ?? 'Cash');
  const [reference, setReference] = useState('');
  const [paidOn, setPaidOn] = useState(localDate());
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');
  const mutation = useRecordBorrowPayment();

  const submit = () => {
    const amt = Math.round(parseFloat(amount) * 100) / 100;
    if (!Number.isFinite(amt) || amt <= 0) return setFormError('Enter an amount greater than zero.');
    if (amt > outstanding) return setFormError(`That is more than the ${money(outstanding)} outstanding.`);
    setFormError('');
    mutation.mutate(
      { id: issue.id, input: { amount: amt, payment_mode: mode, reference_number: reference.trim() || undefined, paid_on: paidOn, notes: notes.trim() || undefined } },
      { onSuccess: (res) => { setAmount((parseFloat(res.issue.item_value) - parseFloat(res.issue.paid_amount)).toFixed(2)); setReference(''); setNotes(''); } }
    );
  };

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2.5 flex items-center gap-1.5 text-[13px] font-semibold"><Wallet size={14} /> Record payment</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField label={`Amount (outstanding ${money(outstanding)})`} required className="sm:col-span-2">
          <Input type="number" min={0.01} max={outstanding} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Payment date" required>
          <Input type="date" min={ymd(issue.issue_date)} max={localDate()} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </FormField>
      </div>
      <div className="mt-3"><PaymentModePicker value={mode} onChange={setMode} modes={settings.allowed_payment_modes} /></div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <FormField label="Reference (UPI / card txn)">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </FormField>
        <FormField label="Notes">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </div>
      <div className="mt-2.5 space-y-2">
        <ErrorBanner message={formError || errorOf(mutation.error)} />
        <Button full loading={mutation.isPending} onClick={submit}>Record {money(parseFloat(amount) || 0)} by {mode}</Button>
      </div>
    </div>
  );
}

// ── Corrections (only before anything is settled) ────────────────

function Corrections({ issue, settings }: { issue: BorrowIssueDetail; settings: BorrowSettings }) {
  const [mode, setMode] = useState<'none' | 'cancel' | 'settlement'>('none');
  const [reason, setReason] = useState('');
  const [value, setValue] = useState(parseFloat(issue.item_value).toFixed(2));
  const cancel = useCancelBorrowIssue();
  const change = useChangeBorrowSettlement();
  const target: SettlementType = issue.settlement_type === 'item_return' ? 'payment' : 'item_return';
  const canSwitch = target === 'payment' ? settings.allow_payment : settings.allow_item_return;

  if (mode === 'none') {
    return (
      <div className="flex flex-wrap gap-2">
        {canSwitch && (
          <Button variant="ghost" size="sm" onClick={() => setMode('settlement')}>
            <Repeat size={13} /> Switch to {SETTLEMENT_LABEL[target].toLowerCase()}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setMode('cancel')}>
          <Ban size={13} /> Cancel record
        </Button>
      </div>
    );
  }

  if (mode === 'cancel') {
    return (
      <div className="space-y-2 rounded-lg border border-danger-soft p-3">
        <div className="text-[12.5px]">Cancelling puts all <strong>{issue.quantity}</strong> unit(s) back onto batch {issue.batch_number}. Use only for a record entered by mistake.</div>
        <FormField label="Reason for cancelling" required>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <ErrorBanner message={errorOf(cancel.error)} />
        <div className="flex gap-2">
          <Button variant="danger" size="sm" disabled={!reason.trim()} loading={cancel.isPending}
            onClick={() => cancel.mutate({ id: issue.id, reason: reason.trim() })}>Cancel record &amp; restock</Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('none')}>Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <div className="text-[12.5px]">Settle this record by <strong>{SETTLEMENT_LABEL[target].toLowerCase()}</strong> instead. Possible only while nothing has been returned or paid.</div>
      {target === 'payment' && (
        <FormField label="Amount to be paid (₹)" required>
          <Input type="number" min={0.01} step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />
        </FormField>
      )}
      <ErrorBanner message={errorOf(change.error)} />
      <div className="flex gap-2">
        <Button size="sm" loading={change.isPending}
          onClick={() => change.mutate(
            { id: issue.id, settlement_type: target, item_value: target === 'payment' ? parseFloat(value) : undefined },
            { onSuccess: () => setMode('none') }
          )}>Switch settlement</Button>
        <Button variant="ghost" size="sm" onClick={() => setMode('none')}>Back</Button>
      </div>
    </div>
  );
}
